import {Block} from './blocks/Block';
import {BlocksPanel} from './blocks/BlocksPanel';
import {ContextAction} from './blocks/ContextAction';
import {ConstructorOfType} from './ConstructorOfType';
import {BuiltInControl} from './controls/BuiltInControl';
import {Control} from './controls/Control';
import {GeneralPanelTab} from './controls/GeneralPanelTab';
import {ModulesPanelTab} from './controls/ModulesPanelTab';
import {SettingsPanelRegistry} from './controls/SettingsPanelRegistry';
import {Extension} from './Extension';
import {ExternalAiAssistant} from './ExternalAiAssistant';
import {ExternalDisplayConditionsLibrary} from './ExternalDisplayConditionsLibrary';
import {ExternalImageLibrary} from './ExternalImageLibrary';
import {ExternalImageLibraryTab} from './ExternalImageLibraryTab';
import {ExternalSmartElementsLibrary} from './ExternalSmartElementsLibrary';
import {ExternalVideosLibrary} from './ExternalVideoLibrary';
import {IconsRegistry} from './icons/IconsRegistry';
import {UIElement} from './ui-elements/UIElement';
import {UIElementTagRegistry} from './ui-elements/UIElementTagRegistry';

export class ExtensionBuilder {
  private i18n?: Record<string, Record<string, string>>;
  private styles: string[] = [];
  private readonly uiElements: ConstructorOfType<UIElement>[] = [];
  private readonly controls: ConstructorOfType<Control | BuiltInControl>[] = [];
  private readonly contextActions: ConstructorOfType<ContextAction>[] = [];
  private readonly blocks: ConstructorOfType<Block>[] = [];
  private readonly generalPanelTabs: ConstructorOfType<GeneralPanelTab>[] = [];
  private readonly modulesPanelTabs: ConstructorOfType<ModulesPanelTab>[] = [];
  private previewStyles?: string;
  private uiElementTagRegistry?: ConstructorOfType<UIElementTagRegistry>;
  private settingsPanelRegistry?: ConstructorOfType<SettingsPanelRegistry>;
  private externalSmartElementsLibrary?: ConstructorOfType<ExternalSmartElementsLibrary>;
  private externalImageLibrary?: ConstructorOfType<ExternalImageLibrary>;
  private externalImageLibraryTab?: ConstructorOfType<ExternalImageLibraryTab>;
  private externalAiAssistant?: ConstructorOfType<ExternalAiAssistant>;
  private externalDisplayConditionsLibrary?: ConstructorOfType<ExternalDisplayConditionsLibrary>;
  private externalVideoLibrary?: ConstructorOfType<ExternalVideosLibrary>;
  private blocksPanel?: ConstructorOfType<BlocksPanel>;
  private iconsRegistry?: ConstructorOfType<IconsRegistry>;

  public withLocalization(i18n: Record<string, Record<string, string>>): this {
    this.i18n = i18n;
    return this;
  }

  /**
   * @deprecated Use addStyles() instead. This method will be removed in a future version.
   */
  public withStyles(styles: string): this {
    this.styles = [styles];
    return this;
  }

  public addStyles(styles: string): this {
    this.styles.push(styles);
    return this;
  }

    /**
     * @description defines custom developer styles to use inside the editor document preview
     */
  public withPreviewStyles(styles: string): this {
    this.previewStyles = styles;
    return this;
  }

  public addContextAction(contextAction: ConstructorOfType<ContextAction>): this {
    this.contextActions.push(contextAction);
    return this;
  }

  public addUiElement(uiElement: ConstructorOfType<UIElement>): this {
    this.uiElements.push(uiElement);
    return this;
  }

  public withUiElementTagRegistry(uiElementTagRegistry: ConstructorOfType<UIElementTagRegistry>): this {
    this.uiElementTagRegistry = uiElementTagRegistry;
    return this;
  }

  public addControl(control: ConstructorOfType<Control | BuiltInControl>): this {
    this.controls.push(control);
    return this;
  }

  public withSettingsPanelRegistry(settingsPanelRegistry: ConstructorOfType<SettingsPanelRegistry>): this {
    this.settingsPanelRegistry = settingsPanelRegistry;
    return this;
  }

  public withExternalSmartElementsLibrary(externalSmartElementsLibrary: ConstructorOfType<ExternalSmartElementsLibrary>): this {
    this.externalSmartElementsLibrary = externalSmartElementsLibrary;
    return this;
  }

  public withExternalImageLibrary(externalImageLibrary: ConstructorOfType<ExternalImageLibrary>): this {
    this.externalImageLibrary = externalImageLibrary;
    return this;
  }

  public withExternalImageLibraryTab(externalImageLibraryTab: ConstructorOfType<ExternalImageLibraryTab>): this {
    this.externalImageLibraryTab = externalImageLibraryTab;
    return this;
  }

  public withExternalAiAssistant(externalAiAssistant: ConstructorOfType<ExternalAiAssistant>): this {
    this.externalAiAssistant = externalAiAssistant;
    return this;
  }

  public withExternalDisplayCondition(externalDisplayCondition: ConstructorOfType<ExternalDisplayConditionsLibrary>): this {
    this.externalDisplayConditionsLibrary = externalDisplayCondition;
    return this;
  }

  public withExternalVideosLibrary(externalVideoLibrary: ConstructorOfType<ExternalVideosLibrary>): this {
    this.externalVideoLibrary = externalVideoLibrary;
    return this;
  }

  public withBlocksPanel(blocksPanel: ConstructorOfType<BlocksPanel>): this {
    this.blocksPanel = blocksPanel;
    return this;
  }

  public addBlock(block: ConstructorOfType<Block>): this {
    this.blocks.push(block);
    return this;
  }

  public withIconsRegistry(iconsRegistry: ConstructorOfType<IconsRegistry>): this {
    this.iconsRegistry = iconsRegistry;
    return this;
  }

  public addGeneralPanelTab(tab: ConstructorOfType<GeneralPanelTab>): this {
    this.generalPanelTabs.push(tab);
    return this;
  }

  public addModulesPanelTab(tab: ConstructorOfType<ModulesPanelTab>): this {
    this.modulesPanelTabs.push(tab);
    return this;
  }

  public build(): Extension {
    return new Extension({
      i18n: this.i18n,
      styles: this.styles.map(style => style.trim()).join('\n'),
      uiElements: this.uiElements,
      uiElementTagRegistry: this.uiElementTagRegistry,
      controls: this.controls,
      settingsPanelRegistry: this.settingsPanelRegistry,
      contextActions: this.contextActions,
      blocks: this.blocks,
      externalSmartElementsLibrary: this.externalSmartElementsLibrary,
      externalImageLibrary: this.externalImageLibrary,
      previewStyles: this.previewStyles,
      externalAiAssistant: this.externalAiAssistant,
      externalDisplayConditionsLibrary: this.externalDisplayConditionsLibrary,
      externalVideoLibrary: this.externalVideoLibrary,
      blocksPanel: this.blocksPanel,
      iconsRegistry: this.iconsRegistry,
      externalImageLibraryTab: this.externalImageLibraryTab,
      generalPanelTabs: this.generalPanelTabs,
      modulesPanelTabs: this.modulesPanelTabs,
    });
  }
}
